import React, { useState, Fragment } from "react";
import Collapse from "react-bootstrap/Collapse";
import { buildGeneratedAnswer } from "@coveo/headless";
import { useCoveoController } from "../utils";
import { GeneratedAnswerBody } from "./GeneratedAnswerBody";
import { NegativeFeedbackModal } from "./NegativeFeedbackModal";
import { Citations } from "./Citations";
import { HeaderActions } from "./HeaderActions";

/**
 * @typedef {import('@coveo/headless').GeneratedAnswer} GeneratedAnswerController
 * @typedef {import('@coveo/headless').GeneratedAnswerState} GeneratedAnswerState
 * @typedef {import('@coveo/headless').GeneratedAnswerCitation} GeneratedAnswerCitation
 * @typedef {import('react').SyntheticEvent} SyntheticEvent
 */

const GeneratedAnswer = () => {
  /**
   * @type {{ controller: GeneratedAnswerController, state: GeneratedAnswerState}}
   */
  const { controller, state } = useCoveoController(
    (headlessEngine) =>
      buildGeneratedAnswer(headlessEngine, {
        initialState: {
          responseFormat: {
            answerStyle: "default",
            contentFormat: ["text/markdown", "text/plain"]
          }
        }
      })
  );
  const [answerCopied, setAnswerCopied] = useState(false);
  const [answerExpanded, setAnswerExpanded] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const {
    isLoading,
    isStreaming,
    isAnswerGenerated,
    answer,
    citations,
    answerContentFormat
  } = state;

  const copyAnswer = () => {
    navigator.clipboard.writeText(answer);

    setAnswerCopied(true);

    setTimeout(() => setAnswerCopied(false), 2000);
  };

  const onThumbsUpClick = () => {
    controller.sendFeedback({ helpful: true });
    controller.like();
  };

  const onThumbsDownClick = () => {
    setShowFeedbackModal(true);
  };

  const onExpand = () => {
    setAnswerExpanded((expanded) => !expanded);
  };

  const onFeedbackModalCancel = () => {
    setShowFeedbackModal(false);
  };

  /**
   * @param {Object} params
   * @param {String} params.feedbackText
   */
  const onFeedbackModalSubmit = ({ feedbackText }) => {
    feedbackText
      ? controller.sendDetailedFeedback(feedbackText)
      : controller.sendFeedback({ helpful: false });
    controller.dislike();

    setShowFeedbackModal(false);
  };

  // We do not show anything until there's an answer streaming. That's our
  // "loading" state
  if (isLoading) return null;

  // Do not render the component if there no answer generated nor streaming ("loading"),
  // or if the resulting answer is falsy
  if (!(isStreaming || isAnswerGenerated) || !answer) return null;

  return (
    <Fragment>
      <aside>
        <article className="gen-answer-container">
          <div className="header">
            <div className="tags-container">
              <span className="tag tag-gen-answer">
                Generated answer for you
              </span>
              <span className="tag tag-beta">Beta</span>
            </div>
            <HeaderActions
              coveoState={state}
              answerExpanded={answerExpanded}
              answerCopied={answerCopied}
              onThumbsUpClick={onThumbsUpClick}
              onThumbsDownClick={onThumbsDownClick}
              onCopyAnswerClick={copyAnswer}
              onExpandClick={onExpand}
            />
          </div>
          <Collapse in={answerExpanded}>
            <div id="gen-answer-content">
              <GeneratedAnswerBody
                answer={answer}
                contentFormat={answerContentFormat}
                isStreaming={isStreaming}
              />
              <div className="footer">
                <Citations citations={citations} />
                <small className="footnote">Generated content may contain errors. Verify important information.</small>
              </div>
            </div>
          </Collapse>
        </article>
      </aside>
      <NegativeFeedbackModal
        show={showFeedbackModal}
        onCancel={onFeedbackModalCancel}
        onSubmit={onFeedbackModalSubmit}
      />
    </Fragment>
  );
};

export { GeneratedAnswer };
